// One managed email list (blacklist or allowlist): a header with an "Add" button,
// a table of entries, and per-row delete behind a confirm dialog. Both sections
// on the page render this with different data + type options.
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Paper, Typography, Button, Chip, IconButton, Table, TableHead, TableBody,
  TableRow, TableCell, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { deleteEmailEntry } from '../../../store/admin-blacklist-slice.js';
import ConfirmDialog from '../ConfirmDialog.jsx';
import AddEntryDialog from './AddEntryDialog.jsx';

const EmailListSection = ({
  list, title, description, entries, typeOptions,
}) => {
  const dispatch = useDispatch();
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleConfirmDelete = async () => {
    await dispatch(deleteEmailEntry({ list, id: pendingDelete.id }));
    setPendingDelete(null);
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1,
      }}
      >
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setAddOpen(true)}>
          Add
        </Button>
      </Box>

      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No entries yet.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pattern</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{entry.pattern}</TableCell>
                <TableCell><Chip label={entry.pattern_type} size="small" /></TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{entry.reason || '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Remove entry">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setPendingDelete(entry)}
                      aria-label={`Remove ${entry.pattern}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AddEntryDialog
        open={addOpen}
        list={list}
        title={`Add ${title.toLowerCase()} entry`}
        typeOptions={typeOptions}
        onClose={() => setAddOpen(false)}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Remove entry?"
        message={pendingDelete
          ? `Remove “${pendingDelete.pattern}” from the ${title.toLowerCase()}? This cannot be undone.`
          : ''}
        confirmLabel="Remove"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Paper>
  );
};

export default EmailListSection;
